import { useMemo } from "react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import {
  LayoutDashboard,
  Users,
  CreditCard,
  CalendarCheck,
  Settings,
  ClipboardList,
  LogOut,
  ScanFace,
  Dumbbell,
  UserCog,
  GraduationCap,
  BookOpen,
  CalendarDays,
  Shield,
  Wallet,
  Landmark,
  X,
  ChevronsLeft,
  ShoppingBag,
  Monitor,
  Building2,
} from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import { Button } from "../ui/Button"
import { fr } from "../../locales/fr"
import { isOwner, isPureCoach } from "../../utils/roles"

function useMainNavigationItems() {
  const { user } = useAuth()
  return useMemo(() => {
    const pureCoach = isPureCoach(user)
    const owner = isOwner(user)
    const items = [{ name: fr.nav.dashboard, href: "/", icon: LayoutDashboard }]
    if (!pureCoach) {
      items.push(
        { name: fr.nav.members, href: "/members", icon: Users },
        { name: fr.nav.payments, href: "/payments", icon: CreditCard },
        { name: fr.nav.products, href: "/products", icon: ShoppingBag },
        { name: fr.nav.subscriptions, href: "/subscriptions", icon: CalendarCheck },
        { name: fr.nav.accessTerminal, href: "/access", icon: ScanFace },
        { name: fr.nav.entranceDisplay, href: "/entrance-display", icon: Monitor },
        { name: fr.nav.accessLogs, href: "/logs", icon: ClipboardList }
      )
    }
    if (!pureCoach) {
      items.push({ name: fr.nav.teamEmployees, href: "/team/employees", icon: UserCog })
    }
    items.push(
      { name: fr.nav.teamCoaches, href: "/team/coaches", icon: GraduationCap },
      { name: fr.nav.courses, href: "/courses", icon: BookOpen },
      { name: fr.nav.courseCalendar, href: "/calendar", icon: CalendarDays }
    )
    if (owner) {
      items.push(
        { name: fr.nav.myGyms, href: "/settings/gyms", icon: Building2 },
        { name: fr.nav.userAccounts, href: "/settings/users", icon: Shield }
      )
    }
    items.push({ name: fr.nav.settings, href: "/settings", icon: Settings })
    return items
  }, [user])
}

/** Dépenses + comptabilité : regroupés en bas de la barre latérale */
function useBottomFinanceItems() {
  const { user } = useAuth()
  return useMemo(() => {
    if (isPureCoach(user)) {
      return []
    }
    return [
      { name: fr.nav.expenses, href: "/expenses", icon: Wallet },
      { name: fr.nav.accounting, href: "/accounting", icon: Landmark },
    ]
  }, [user])
}

function navLinkClass(isActive) {
  return `
    flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors
    ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}
  `
}

function isNavActive(pathname, href) {
  return href === "/"
    ? pathname === "/"
    : pathname === href || pathname.startsWith(`${href}/`)
}

export default function Sidebar({ onNavigate, onCollapseDesktop }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const mainNav = useMainNavigationItems()
  const bottomFinance = useBottomFinanceItems()

  const afterNav = () => {
    onNavigate?.()
  }

  return (
    <aside className="flex h-full min-h-0 w-full flex-col overflow-y-auto border-r border-border bg-card">
      <div className="flex h-16 items-center gap-3 border-b border-border px-4 sm:px-6">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary">
          <Dumbbell className="h-5 w-5 text-primary-foreground" aria-hidden />
        </div>
        <span className="min-w-0 flex-1 truncate text-lg font-bold text-foreground">{fr.brand}</span>
        {onCollapseDesktop && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="hidden shrink-0 lg:inline-flex"
            onClick={onCollapseDesktop}
            aria-label={fr.navbar.collapseSidebar}
          >
            <ChevronsLeft className="h-5 w-5" />
          </Button>
        )}
        {onNavigate && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            onClick={afterNav}
            aria-label={fr.navbar.closeMenu}
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4">
        {mainNav.map((item) => {
          const isActive = isNavActive(location.pathname, item.href)
          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={afterNav}
              className={navLinkClass(isActive)}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </NavLink>
          )
        })}
      </nav>

      {bottomFinance.length > 0 && (
        <div className="shrink-0 space-y-1 border-t border-border px-3 pb-2 pt-3">
          {bottomFinance.map((item) => {
            const isActive = isNavActive(location.pathname, item.href)
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={afterNav}
                className={navLinkClass(isActive)}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            )
          })}
        </div>
      )}

      <div className="shrink-0 border-t border-border p-4">
        <Button
          type="button"
          variant="ghost"
          className="h-auto w-full justify-start gap-3 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
          onClick={() => {
            afterNav()
            logout()
            navigate("/login", { replace: true })
          }}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {fr.nav.logout}
        </Button>
      </div>
    </aside>
  )
}
